import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Drawer,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  Switch,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ArrowBack,
  Dashboard,
  Visibility,
  People as PeopleIcon,
  Person as PersonIcon,
  AccountBalance,
  Assignment,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

const PLAN_COLORS = { trial: 'warning', basic: 'info', pro: 'success' };
const STATUS_COLORS = { active: 'success', expired: 'error', suspended: 'default' };

const HEALTH_BAND_COLOR = {
  critical:     { bg: '#fee2e2', text: '#991b1b' },
  dormant:      { bg: '#f3f4f6', text: '#374151' },
  at_risk:      { bg: '#fef3c7', text: '#92400e' },
  provisioning: { bg: '#e0e7ff', text: '#3730a3' },
  healthy:      { bg: '#d1fae5', text: '#065f46' },
};
const HEALTH_BAND_LABEL = {
  critical: 'Critical', dormant: 'Dormant', at_risk: 'At risk',
  provisioning: 'Provisioning', healthy: 'Healthy',
};
const HEALTH_COMPONENT_LABEL = {
  recency: 'Recent activity', activeRatio: 'Active-user ratio', adoption: 'Feature adoption',
  onboarding: 'Onboarding', subscription: 'Subscription standing',
};

const toDateInputValue = (isoDate) => (isoDate ? new Date(isoDate).toISOString().slice(0, 10) : '');
const toIsoDateTime = (dateOnly) => (dateOnly ? `${dateOnly}T00:00:00` : null);

const currentAcademicYear = () => {
  const now = new Date();
  return now.getMonth() >= 3
    ? `${now.getFullYear()}-${now.getFullYear() + 1}`
    : `${now.getFullYear() - 1}-${now.getFullYear()}`;
};

const academicYearOptions = (() => {
  const current = new Date().getFullYear();
  return Array.from({ length: 4 }, (_, i) => {
    const yr = current - 1 + i;
    return `${yr}-${yr + 1}`;
  });
})();

const EMPTY_FORM = {
  plan_tier: 'trial',
  status: 'active',
  trial_ends_at: '',
  current_period_start: '',
  current_period_end: '',
  amount_paid: '',
  payment_date: '',
  payment_reference: '',
  notes: ''
};

const SchoolDetail = () => {
  const navigate = useNavigate();
  const { user, logout, startImpersonation, isImpersonating } = useAuth();
  const [impersonating, setImpersonating] = useState(false);
  const { id } = useParams();
  const [school, setSchool] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [featureDefs, setFeatureDefs] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});
  const [savingFeatureKey, setSavingFeatureKey] = useState(null);
  const [exportYear, setExportYear] = useState(currentAcademicYear());
  const [downloading, setDownloading] = useState(null); // 'students' | 'teachers' | 'fees' | 'attendance' | 'bundle' | null
  const [tierMatrix, setTierMatrix] = useState(null); // { tiers, features }
  const [tierDiff, setTierDiff] = useState(null); // preview changes for the plan_tier currently selected in the drawer
  const [applyTierDialogOpen, setApplyTierDialogOpen] = useState(false);
  const [applyTierPreview, setApplyTierPreview] = useState(null);
  const [applyingTier, setApplyingTier] = useState(false);
  const [health, setHealth] = useState(null);

  const fetchSchool = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [schoolRes, subRes, costsRes, flagsRes, tiersRes, healthRes] = await Promise.all([
        superAdminAPI.getSchool(id),
        superAdminAPI.getSchoolSubscription(id).catch(() => null),
        superAdminAPI.getSchoolApiCosts(id).catch(() => null),
        superAdminAPI.getFeatureFlags().catch(() => null),
        superAdminAPI.getPlanTiers().catch(() => null),
        superAdminAPI.getSchoolHealth(id).catch(() => null),
      ]);
      setSchool(schoolRes.data.school);
      setSubscription(subRes ? subRes.data.subscription : null);
      setCosts(costsRes ? costsRes.data.costs : null);
      if (flagsRes) {
        setFeatureDefs(flagsRes.data.features || []);
        const schoolEntry = flagsRes.data.schools.find((s) => s.schoolId === id);
        setFeatureFlags(schoolEntry?.flags || {});
      }
      if (tiersRes) {
        setTierMatrix({ tiers: tiersRes.data.tiers, features: tiersRes.data.features });
      }
      setHealth(healthRes ? healthRes.data.health : null);
    } catch (error) {
      toast.error('Failed to load school details');
      console.error(error);
      navigate('/superadmin/schools');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const handleToggleFeature = async (featureKey, nextValue) => {
    setSavingFeatureKey(featureKey);
    setFeatureFlags((prev) => ({ ...prev, [featureKey]: nextValue }));
    try {
      await superAdminAPI.updateFeatureFlag(id, featureKey, nextValue);
    } catch (error) {
      toast.error('Failed to update feature toggle');
      console.error(error);
      setFeatureFlags((prev) => ({ ...prev, [featureKey]: !nextValue }));
    } finally {
      setSavingFeatureKey(null);
    }
  };

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  const openApplyTierDialog = async () => {
    const tier = subscription?.plan_tier || 'trial';
    setApplyTierDialogOpen(true);
    setApplyTierPreview(null);
    try {
      const res = await superAdminAPI.previewSchoolTier(id, tier);
      setApplyTierPreview(res.data.changes);
    } catch (error) {
      toast.error('Failed to preview tier defaults');
      console.error(error);
    }
  };

  const handleApplyTier = async () => {
    const tier = subscription?.plan_tier || 'trial';
    setApplyingTier(true);
    try {
      const res = await superAdminAPI.applySchoolTier(id, tier);
      setFeatureFlags(res.data.flags);
      toast.success(`${tier} defaults applied`);
      setApplyTierDialogOpen(false);
    } catch (error) {
      toast.error('Failed to apply tier defaults');
      console.error(error);
    } finally {
      setApplyingTier(false);
    }
  };

  const openEdit = () => {
    setForm({
      plan_tier: subscription?.plan_tier || 'trial',
      status: subscription?.status || 'active',
      trial_ends_at: toDateInputValue(subscription?.trial_ends_at),
      current_period_start: toDateInputValue(subscription?.current_period_start),
      current_period_end: toDateInputValue(subscription?.current_period_end),
      amount_paid: subscription?.amount_paid || '',
      payment_date: toDateInputValue(subscription?.payment_date),
      payment_reference: subscription?.payment_reference || '',
      notes: subscription?.notes || ''
    });
    setTierDiff(null);
    setDrawerOpen(true);
  };

  const handlePlanTierChange = async (newTier) => {
    setForm((f) => ({ ...f, plan_tier: newTier }));
    if (newTier === (subscription?.plan_tier || 'trial')) {
      setTierDiff(null);
      return;
    }
    try {
      const res = await superAdminAPI.previewSchoolTier(id, newTier);
      setTierDiff(res.data.changes);
    } catch (error) {
      setTierDiff(null);
    }
  };

  const handleSaveSubscription = async () => {
    setSaving(true);
    try {
      const response = await superAdminAPI.updateSchoolSubscription(id, {
        plan_tier: form.plan_tier,
        status: form.status,
        trial_ends_at: toIsoDateTime(form.trial_ends_at),
        current_period_start: toIsoDateTime(form.current_period_start),
        current_period_end: toIsoDateTime(form.current_period_end),
        amount_paid: form.amount_paid === '' ? null : form.amount_paid,
        payment_date: form.payment_date || null,
        payment_reference: form.payment_reference,
        notes: form.notes
      });
      setSubscription(response.data.subscription);
      if (tierDiff && tierDiff.length > 0) {
        toast.success(`Subscription updated — ${tierDiff.length} feature toggle${tierDiff.length === 1 ? '' : 's'} re-applied for ${form.plan_tier}`);
        fetchSchool();
      } else {
        toast.success('Subscription updated');
      }
      setDrawerOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update subscription');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleImpersonate = async () => {
    if (isImpersonating()) {
      toast.warning('You are already in an impersonation session. Exit it first.');
      return;
    }
    setImpersonating(true);
    const result = await startImpersonation(school.id);
    setImpersonating(false);

    if (result.success) {
      toast.success(`Viewing as ${school.name}`);
      navigate('/principal/dashboard');
    } else {
      toast.error(result.error);
    }
  };

  const handleExportDataset = async (dataset) => {
    setDownloading(dataset);
    try {
      await superAdminAPI.exportSchoolDataset(school.id, dataset, exportYear);
    } catch (error) {
      toast.error(`Failed to export ${dataset}`);
      console.error(error);
    } finally {
      setDownloading(null);
    }
  };

  const handleExportBundle = async () => {
    setDownloading('bundle');
    try {
      await superAdminAPI.exportSchoolBundle(school.id, exportYear);
    } catch (error) {
      toast.error('Failed to generate ZIP bundle');
      console.error(error);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!school) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      <Paper sx={{ p: 4 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            {school.name}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Tooltip title={school.status === 'Inactive' ? 'School is inactive — activate it first' : ''}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={impersonating ? <CircularProgress size={16} /> : <Visibility />}
                  onClick={handleImpersonate}
                  disabled={impersonating || school.status === 'Inactive'}
                  sx={{
                    borderColor: '#7C3AED',
                    color: '#7C3AED',
                    '&:hover': { bgcolor: '#7C3AED10', borderColor: '#6D28D9' },
                  }}
                >
                  {impersonating ? 'Opening…' : 'View as School'}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              onClick={() => navigate(`/superadmin/schools/${id}/edit`)}
            >
              Edit School
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Basic Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="School Name" value={school.name} />
                  <InfoRow label="Status" value={school.status} />
                  <InfoRow label="Created" value={new Date(school.created_at).toLocaleDateString()} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Contact Information
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="Contact Person" value={school.contact_person} />
                  <InfoRow label="Email" value={school.contact_email} />
                  <InfoRow label="Phone" value={school.contact_phone} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Address
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="Street Address" value={school.address} />
                  <InfoRow label="City" value={school.city} />
                  <InfoRow label="State" value={school.state} />
                  <InfoRow label="Postal Code" value={school.postal_code} />
                  <InfoRow label="Country" value={school.country} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Subscription */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="bold">Subscription</Typography>
                  <Button size="small" onClick={openEdit}>
                    {subscription ? 'Edit' : 'Add subscription'}
                  </Button>
                </Box>
                {subscription ? (
                  <Box sx={{ mt: 2 }}>
                    <InfoRow
                      label="Plan"
                      value={<Chip label={subscription.plan_tier} color={PLAN_COLORS[subscription.plan_tier]} size="small" />}
                    />
                    <InfoRow
                      label="Status"
                      value={<Chip label={subscription.status} color={STATUS_COLORS[subscription.status]} size="small" />}
                    />
                    <InfoRow
                      label="Period end"
                      value={
                        subscription.current_period_end
                          ? new Date(subscription.current_period_end).toLocaleDateString('en-IN')
                          : subscription.trial_ends_at
                            ? `Trial until ${new Date(subscription.trial_ends_at).toLocaleDateString('en-IN')}`
                            : 'N/A'
                      }
                    />
                    <InfoRow
                      label="Last payment"
                      value={
                        subscription.amount_paid
                          ? `₹${Number(subscription.amount_paid).toLocaleString('en-IN')} on ${new Date(subscription.payment_date).toLocaleDateString('en-IN')}`
                          : 'N/A'
                      }
                    />
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No subscription on record
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* API usage */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  API usage this month
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <InfoRow label="Chatbot queries" value={costs?.chatbot_queries ?? '—'} />
                  <InfoRow
                    label="AI tokens used"
                    value={costs?.tokens_this_month ? costs.tokens_this_month.toLocaleString('en-IN') : '—'}
                  />
                  <InfoRow label="WhatsApp messages" value={costs?.whatsapp_this_month ?? '—'} />
                  <InfoRow label="Emails sent" value={costs?.emails_this_month ?? '—'} />
                  <InfoRow
                    label="Estimated cost"
                    value={
                      costs?.total_cost_this_month_inr != null
                        ? `₹${Number(costs.total_cost_this_month_inr).toLocaleString('en-IN')} (~$${costs.total_cost_this_month_usd})`
                        : '—'
                    }
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* School Health */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  School Health
                </Typography>
                {!health ? (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    Health data unavailable
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={1.5}>
                      <Typography variant="h4" fontWeight="bold">{health.score}</Typography>
                      <Chip
                        label={HEALTH_BAND_LABEL[health.band] ?? health.band}
                        size="small"
                        sx={{
                          bgcolor: (HEALTH_BAND_COLOR[health.band] ?? {}).bg,
                          color: (HEALTH_BAND_COLOR[health.band] ?? {}).text,
                        }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={health.score}
                      sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
                      color={health.score >= 70 ? 'success' : health.score >= 40 ? 'warning' : 'error'}
                    />
                    {health.reasons?.length > 0 && (
                      <Box mb={1.5}>
                        {health.reasons.map((r, i) => (
                          <Typography key={i} variant="caption" color="text.secondary" display="block">• {r}</Typography>
                        ))}
                      </Box>
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1}>
                      {Object.entries(health.breakdown || {}).map(([key, b]) => (
                        <Box key={key} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">{HEALTH_COMPONENT_LABEL[key] ?? key}</Typography>
                          <Typography variant="body2" fontWeight={600}>{b.score}/{b.max}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                      Last active: {health.signals?.last_active_at ? new Date(health.signals.last_active_at).toLocaleString('en-IN') : 'Never'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Export Data */}
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>Export school data</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Download records for data portability or school migration.
                    </Typography>
                  </Box>
                  <Chip
                    label="DPDP Act"
                    size="small"
                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem' }}
                  />
                </Box>

                <FormControl size="small" sx={{ minWidth: 160, mb: 2 }}>
                  <InputLabel>Academic year</InputLabel>
                  <Select value={exportYear} label="Academic year"
                    onChange={(e) => setExportYear(e.target.value)}>
                    {academicYearOptions.map((y) => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {[
                    { label: 'Students', dataset: 'students', icon: <PeopleIcon sx={{ fontSize: 14 }} />, color: '#4F46E5' },
                    { label: 'Teachers', dataset: 'teachers', icon: <PersonIcon sx={{ fontSize: 14 }} />, color: '#0891b2' },
                    { label: 'Fees', dataset: 'fees', icon: <AccountBalance sx={{ fontSize: 14 }} />, color: '#059669' },
                    { label: 'Attendance', dataset: 'attendance', icon: <Assignment sx={{ fontSize: 14 }} />, color: '#d97706' },
                  ].map(({ label, dataset, icon, color }) => (
                    <Button
                      key={dataset}
                      size="small"
                      variant="outlined"
                      startIcon={downloading === dataset ? <CircularProgress size={12} /> : icon}
                      disabled={!!downloading}
                      onClick={() => handleExportDataset(dataset)}
                      sx={{ borderColor: color, color, '&:hover': { bgcolor: color + '10', borderColor: color } }}
                    >
                      {label} CSV
                    </Button>
                  ))}
                </Box>

                <Button
                  variant="contained"
                  startIcon={downloading === 'bundle' ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                  disabled={!!downloading}
                  onClick={handleExportBundle}
                  fullWidth
                  sx={{ bgcolor: '#4F46E5', '&:hover': { bgcolor: '#3730A3' } }}
                >
                  {downloading === 'bundle' ? 'Preparing ZIP…' : 'Download All (ZIP)'}
                </Button>

                <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                  Includes README with DPDP Act data handling notice. Attendance filtered to {exportYear}.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Feature Flags */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">Feature Flags</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enable or disable modules for this school. A missing toggle defaults to enabled.
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={openApplyTierDialog}>
                    Apply {subscription?.plan_tier || 'trial'} defaults
                  </Button>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Stack divider={<Divider />}>
                  {featureDefs.map((f) => (
                    <Box
                      key={f.key}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 1.25 }}
                    >
                      <Typography variant="body2">{f.label}</Typography>
                      <Switch
                        checked={featureFlags[f.key] !== false}
                        disabled={savingFeatureKey === f.key}
                        onChange={(e) => handleToggleFeature(f.key, e.target.checked)}
                        color="primary"
                      />
                    </Box>
                  ))}
                </Stack>

                {tierMatrix && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Plan tier matrix (reference)
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Feature</strong></TableCell>
                            <TableCell align="center"><strong>Trial</strong></TableCell>
                            <TableCell align="center"><strong>Basic</strong></TableCell>
                            <TableCell align="center"><strong>Pro</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tierMatrix.features.map((f) => (
                            <TableRow key={f.key}>
                              <TableCell>{f.label}</TableCell>
                              {['trial', 'basic', 'pro'].map((tier) => (
                                <TableCell align="center" key={tier}>
                                  {(tierMatrix.tiers[tier] || []).includes(f.key) ? (
                                    <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  ) : (
                                    <CancelIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={applyTierDialogOpen} onClose={applyingTier ? undefined : () => setApplyTierDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Apply {subscription?.plan_tier || 'trial'} defaults?</DialogTitle>
        <DialogContent>
          {applyTierPreview === null ? (
            <Box display="flex" alignItems="center" gap={2} py={2}>
              <CircularProgress size={20} />
              <Typography>Checking what would change…</Typography>
            </Box>
          ) : applyTierPreview.length === 0 ? (
            <Typography>No changes — feature flags already match the {subscription?.plan_tier || 'trial'} baseline.</Typography>
          ) : (
            <>
              <Typography sx={{ mb: 1.5 }}>
                This will write an explicit toggle for every feature, overriding any manual changes made since the last tier apply:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {applyTierPreview.map((c) => (
                  <li key={c.feature_key}>
                    <Typography variant="body2">{c.feature_key}: {c.to ? 'enabled' : 'disabled'}</Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyTierDialogOpen(false)} disabled={applyingTier}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApplyTier}
            disabled={applyingTier || applyTierPreview === null || applyTierPreview.length === 0}
          >
            {applyingTier ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={drawerOpen} onClose={saving ? undefined : () => setDrawerOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 400 }, p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {school.name} — Subscription
          </Typography>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField select label="Plan tier" value={form.plan_tier} onChange={(e) => handlePlanTierChange(e.target.value)}>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="basic">Basic</MenuItem>
              <MenuItem value="pro">Pro</MenuItem>
            </TextField>
            {tierDiff && (
              tierDiff.length === 0 ? (
                <Alert severity="info">No feature changes for {form.plan_tier}.</Alert>
              ) : (
                <Alert severity="warning">
                  Switching to <strong>{form.plan_tier}</strong> will change {tierDiff.length} feature{tierDiff.length === 1 ? '' : 's'} on save:
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                    {tierDiff.map((c) => (
                      <li key={c.feature_key}>
                        <Typography variant="caption">
                          {c.feature_key}: {c.to ? 'enabled' : 'disabled'}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              )
            )}
            <TextField select label="Subscription status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>
            {form.plan_tier === 'trial' && (
              <TextField
                label="Trial ends"
                type="date"
                value={form.trial_ends_at}
                onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            )}
            <TextField
              label="Period start"
              type="date"
              value={form.current_period_start}
              onChange={(e) => setForm({ ...form, current_period_start: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Period end"
              type="date"
              value={form.current_period_end}
              onChange={(e) => setForm({ ...form, current_period_end: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Amount paid (₹)"
              type="number"
              value={form.amount_paid}
              onChange={(e) => setForm({ ...form, amount_paid: e.target.value })}
            />
            <TextField
              label="Payment date"
              type="date"
              value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Payment reference"
              value={form.payment_reference}
              onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
              placeholder="e.g. RAZORPAY-123 or NEFT-REF"
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              multiline
              minRows={3}
              helperText="Internal notes, not visible to the school"
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setDrawerOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveSubscription} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </Container>
  );
};

// Helper component for displaying information rows
const InfoRow = ({ label, value }) => (
  <Box display="flex" py={1}>
    <Typography color="text.secondary" sx={{ minWidth: 150 }}>
      {label}:
    </Typography>
    <Typography fontWeight="medium">{value === 0 ? 0 : (value || 'N/A')}</Typography>
  </Box>
);

export default SchoolDetail;
